/**
 * CompanyAccountPage — owner-only financial summary.
 *
 *   Server: GET /api/cargo/me/company/money/summary
 *
 * Aggregates:
 *   1. by_party  — 6-party ledger split (driver/center/sender/receiver/etc)
 *   2. escrow    — open / settled / clawed-back per currency
 *   3. open_obligations — pending cash to/from parties
 *
 * Mirrors Flutter `company/company_account_page.dart`. Members do NOT
 * see this; server enforces NOT_COMPANY_OWNER.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, DollarSign, TrendingUp, AlertCircle, Loader, Wallet, RotateCcw } from 'lucide-react';
import { useMyCompany, useCompanyMoneySummary } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import { TextField } from '@/components/common/TextField';
import { Button } from '@/components/common/Button';
import type { MoneySummaryRow } from '@/types/companyExtras';

export default function CompanyAccountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: company, isLoading: companyLoading } = useMyCompany();

  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [currency, setCurrency] = useState<string>('');
  const [appliedFilters, setAppliedFilters] = useState<{ from?: string; to?: string; currency?: string }>({});

  const { data, isLoading } = useCompanyMoneySummary(appliedFilters);

  if (companyLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!company) {
    navigate('/company', { replace: true });
    return null;
  }

  const apply = () => setAppliedFilters({
    from: from || undefined,
    to: to || undefined,
    currency: currency.trim().toUpperCase() || undefined,
  });
  const reset = () => {
    setFrom(''); setTo(''); setCurrency('');
    setAppliedFilters({});
  };

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
          <Wallet size={20} /> {t('company_account_title', 'الحساب المالي')}
        </h1>
        <p className="text-text-secondary text-sm mb-5">
          {t('company_account_subtitle', 'كل أرباح ومستحقات شركتك من الشحنات المُختومة باسمها.')}
        </p>

        {/* Filters */}
        <div className="bg-surface border border-border rounded-2xl p-4 mb-4">
          <h3 className="text-sm font-bold text-text-secondary mb-3">
            {t('filters', 'فلترة')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <TextField
              label={t('from_date', 'من تاريخ')}
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              dir="ltr"
            />
            <TextField
              label={t('to_date', 'إلى تاريخ')}
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              dir="ltr"
            />
            <TextField
              label={t('currency', 'العملة')}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              placeholder="SYP"
              dir="ltr"
            />
            <div className="flex items-end gap-2">
              <Button variant="primary" onClick={apply} fullWidth>
                {t('apply', 'تطبيق')}
              </Button>
              <Button variant="ghost" onClick={reset} aria-label="reset" type="button">
                <RotateCcw size={16} />
              </Button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12 text-text-tertiary">
            <Loader size={28} className="animate-spin" />
          </div>
        ) : data ? (
          <>
            <Section
              icon={<TrendingUp size={16} />}
              title={t('account_by_party', 'الأرباح والعمولات (حسب الطرف)')}
              rows={data.ledger_by_party}
              showStatus
              showParty
            />
            <Section
              icon={<DollarSign size={16} />}
              title={t('account_escrow', 'المبالغ المحجوزة (Escrow)')}
              rows={data.escrow_by_status}
              showStatus
            />
            <Section
              icon={<AlertCircle size={16} />}
              title={t('account_obligations', 'مستحقات معلّقة')}
              rows={data.open_obligations}
              showFromType
              warning
            />
          </>
        ) : (
          <div className="bg-surface border border-border rounded-2xl p-10 text-center">
            <p className="text-text-secondary">{t('no_data', 'لا بيانات للعرض.')}</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function Section({
  icon,
  title,
  rows,
  showStatus,
  showParty,
  showFromType,
  warning,
}: {
  icon: React.ReactNode;
  title: string;
  rows: MoneySummaryRow[];
  showStatus?: boolean;
  showParty?: boolean;
  showFromType?: boolean;
  warning?: boolean;
}) {
  const { t } = useTranslation();
  if (!rows || rows.length === 0) return null;

  // Group by currency for headline totals
  const totals = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.currency] = (acc[r.currency] || 0) + Number(r.amount_total || 0);
    return acc;
  }, {});

  return (
    <div className={`bg-surface border ${warning ? 'border-warning/40' : 'border-border'} rounded-2xl p-4 mb-4`}>
      <h3 className="text-sm font-bold text-text-primary mb-3 flex items-center gap-2">
        {icon} {title}
      </h3>

      {/* Per-currency totals */}
      <div className="flex flex-wrap gap-2 mb-3">
        {Object.entries(totals).map(([cur, total]) => (
          <div key={cur} className={`px-3 py-1.5 rounded-lg text-sm font-bold ${
            warning ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'
          }`}>
            {Number(total).toLocaleString()} {cur}
          </div>
        ))}
      </div>

      {/* Detail table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-xs text-text-secondary">
              {showParty && <th className="text-right py-2 px-2">{t('party', 'الطرف')}</th>}
              {showFromType && <th className="text-right py-2 px-2">{t('party', 'الطرف')}</th>}
              {showStatus && <th className="text-right py-2 px-2">{t('status', 'الحالة')}</th>}
              <th className="text-right py-2 px-2">{t('currency', 'العملة')}</th>
              <th className="text-right py-2 px-2">{t('rows', 'عدد')}</th>
              <th className="text-right py-2 px-2">{t('total', 'الإجمالي')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border/50">
                {showParty && <td className="py-2 px-2">{r.party_type || '—'}</td>}
                {showFromType && <td className="py-2 px-2">{r.from_type || '—'}</td>}
                {showStatus && <td className="py-2 px-2">{r.status || '—'}</td>}
                <td className="py-2 px-2" dir="ltr">{r.currency}</td>
                <td className="py-2 px-2">{r.row_count}</td>
                <td className="py-2 px-2 font-bold">{Number(r.amount_total || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
